<template>
    <tags-input ref="tagInput" v-model="tags" :existing-tags="computedExistingTags" text-field="label" id-field="key" :typeahead="true"
        :placeholder="placeholder" :validate="validatePlainText" :before-adding-tag="beforeAddingTag"
        @tag-removed="onTagRemoved" />
</template>

<script>
export default {
    props: {
        value: {
            type: Array,
            required: false,
            default: () => [],
        },
        users: {
            type: Array,
            default: () => [],
        },
        groups: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'Add assignees...',
        },
        allowEmails: {
            type: Boolean,
            default: true,
        },
        allowUsers: {
            type: Boolean,
            default: true,
        },
        allowGroups: {
            type: Boolean,
            default: true,
        },
    },

    mounted() {
        this.$on('reset', this.reset);
    },

    beforeDestroy() {
        this.$off('reset');
    },

    data() {
        return {
            tags: [...this.value],
            seenEmails: new Set(),
        };
    },

    watch: {
        tags: {
            handler(newTags) {
                if (JSON.stringify(newTags) !== JSON.stringify(this.value)) {
                    this.$emit('input', newTags);
                }
            },
            deep: true,
        },
        value: {
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.tags)) {
                    this.tags = [...newValue];
                    this.$nextTick(() => {
                        this.resetTracking();
                    });
                }
            },
            deep: true,
        },
    },

    computed: {
        computedExistingTags() {
            const existingKeys = new Set(this.tags.map(tag => JSON.stringify(tag.key)));
            return [
                ...this.filterSelectableTags(
                    this.groups,
                    'group',
                    group => ({
                        id: group.id,
                        key: { type: 'group', value: group.id },
                        label: group.name,
                    }),
                    existingKeys
                ),
                ...this.filterSelectableTags(
                    this.users,
                    'user',
                    user => ({
                        id: user.id,
                        key: { type: 'user', value: user.id, email: user.email },
                        label: `${user.name} (${user.email})`,
                    }),
                    existingKeys
                ),
            ];
        },
    },

    methods: {
        sortTags(tags) {
            const order = { group: 1, user: 2, email: 3 };
            return [...tags].sort((a, b) => {
                const typeA = order[a.key.type] || 99; // Assign default high value if type is unrecognized
                const typeB = order[b.key.type] || 99;
                return typeA - typeB;
            });
        },
        filterSelectableTags(items, type, mapFn, existingKeys) {
            return items
                .map(mapFn)
                .filter(tag => {
                    const tagKey = JSON.stringify(tag.key);
                    return !existingKeys.has(tagKey);
                });
        },

        validatePlainText(tag) {
            if (!this.allowEmails) {
                this.$emit('error', 'Emails are not allowed');
                return false;
            }

            if (this.isEmail(tag)) {
                return true;
            }

            this.$emit('error', 'Invalid email format');
            return false;
        },

        beforeAddingTag(tag) {
            if (typeof tag !== 'object') {
                this.$emit('message', 'Invalid input format', 'danger');
                return false;
            }

            if (!this.isValidTag(tag)) {
                this.$emit('message', 'Duplicate or invalid tag', 'danger');
                return false;
            }

            return true;
        },

        isValidTag(tag) {
            const { key, label } = tag;
            const { type, value } = key

            //Assume plaintext input
            if (key === '' && label.length > 0 && this.allowEmails) {
                if (this.seenEmails.has(label)) {
                    return false; // Email already exists
                }
                this.seenEmails.add(label);
                tag.key = { type: 'email', value: label };
                return true;
            }

            //Email input from pre-existing tag
            if (type === 'email' && this.allowEmails) {
                if (this.seenEmails.has(value)) {
                    return false;
                }
                this.seenEmails.add(value);
                return true;
            }

            if (type === 'user' && this.allowUsers) {
                const user = this.findItemById(this.users, value);
                if (user && !this.seenEmails.has(user.email)) {
                    return true;
                }
            }

            if (type === 'group' && this.allowGroups) {
                const group = this.findItemById(this.groups, value);

                //Check if group with the given id exists multiple times
                if (this.tags.filter(tag => tag.key.type === 'group' && tag.key.value === value).length > 1) {
                    this.tags.splice(this.tags.findIndex(tag => tag.key.type === 'group' && tag.key.value === value), 1);
                    return false;
                }

                if (group) {
                    this.resolveGroupMembers(group);
                    return true;
                }
            }

            return false;
        },

        resolveGroupMembers(group) {
            if (!group.users || !Array.isArray(group.users)) return;

            group.users.forEach(member => {
                if (member.email) {
                    // If email is provided, add directly
                    if (!this.seenEmails.has(member.email)) {
                        this.seenEmails.add(member.email);
                    }
                } else if (member.user_id) {
                    // Resolve user_id via Spark.state.currentTeam.users
                    const user = this.findItemById(this.users, member.user_id);
                    if (user && user.email && !this.seenEmails.has(user.email)) {
                        this.seenEmails.add(user.email);
                    }
                }
            });
        },

        onTagRemoved(tag) {
            const { type, value } = tag.key;

            if (type === 'email') {
                this.seenEmails.delete(value);
            } else if (type === 'user') {
                const user = this.findItemById(this.users, value);
                if (user) {
                    this.seenEmails.delete(user.email);
                }
            } else if (type === 'group') {
                const group = this.findItemById(this.groups, value);
                if (group) {
                    this.removeGroupEmails(group);
                }
            }

            this.$emit('tag-removed', tag);
        },

        removeGroupEmails(group) {
            if (!group.users || !Array.isArray(group.users)) return;

            group.users.forEach(member => {
                if (member.email) {
                    this.seenEmails.delete(member.email);
                } else if (member.user_id) {
                    const user = this.findItemById(this.users, member.user_id);
                    if (user && user.email) {
                        this.seenEmails.delete(user.email);
                    }
                }
            });
        },

        resetTracking() {
            this.seenEmails.clear();

            this.tags.forEach(tag => {
                const { type, value } = tag.key;

                if (type === 'email') {
                    this.seenEmails.add(value);
                } else if (type === 'user') {
                    const user = this.findItemById(this.users, value);
                    if (user) {
                        this.seenEmails.add(user.email);
                    }
                } else if (type === 'group') {
                    const group = this.findItemById(this.groups, value);
                    if (group) {
                        this.resolveGroupMembers(group);
                    }
                }
            });
        },

        findItemById(items, id) {
            return items.find(item => item.id === id);
        },

        isEmail(value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailPattern.test(value);
        },

        reset() {
            this.tags = [];
            this.resetTracking();
            this.$refs.tagInput.input = '';
            this.$refs.tagInput.oldInput = '';
        },
    },
};
</script>
